<template>
  <MedicationAlarmDialog
    v-if="
      inbox.selected?.code === NOTIFICATION_CODE.MEDICATION_REMINDER && inbox.selected.actionable
    "
    v-model:open="open"
    :notification="inbox.selected"
    :saving="saving"
    @respond="respond"
  />
  <NotificationDetailSheet
    v-else
    v-model:open="open"
    :notification="inbox.selected"
    :saving="saving"
    @respond="respond"
  />
</template>

<script setup>
// 1. Import necessary components
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { ToastService } from '@/shared/utilities/feedback/toast-service';
import { useNotificationsStore } from '@/core/stores/notifications.store';
import { useAuthStore } from '@/core/stores/auth.store';
import { useNotificationEvents } from '../composables/useNotificationEvents';
import { NOTIFICATION_CODE, MEDICATION_NOTIFICATION_ACTION } from '../constants/notification';
import notificationsService from '@/shared/services/notifications.service';
import medicationRemindersService from '@/shared/services/medication-reminders.service';
import MedicationAlarmDialog from './MedicationAlarmDialog.vue';
import NotificationDetailSheet from './NotificationDetailSheet.vue';

// 2. Use
const inbox = useNotificationsStore();
const auth = useAuthStore();
const { t, locale } = useI18n();
const notificationEvents = useNotificationEvents();

// 3. Define a reactive variable
const saving = ref(false);
let responseRequest = null;

// 4. Define methods or computed properties if needed
const open = computed({
  get: () => Boolean(inbox.selected),
  set: (value) => {
    if (!value) {
      inbox.selected = null;
    }
  },
});
async function respond(action) {
  if (saving.value || !inbox.selected) {
    return;
  }
  const session = auth.authStateVersion;
  const selected = inbox.selected;
  // Retain the intended operation's ID if the server commits but its response is lost.
  if (responseRequest?.action !== action) {
    responseRequest = { action, requestId: crypto.randomUUID() };
  }
  saving.value = true;
  try {
    let service = notificationsService;
    if (selected.code === NOTIFICATION_CODE.MEDICATION_REMINDER) {
      service = medicationRemindersService;
    }
    const result = await service.respond(selected.itemUnique, responseRequest);
    if (inbox.selected === selected) {
      inbox.selected = result;
      if (result.alreadyAnswered) {
        ToastService.info(t('nkf.notificationDetails.alreadyAnswered'));
      }
      if (selected.code === NOTIFICATION_CODE.MEDICATION_REMINDER) {
        inbox.selected = null;
        if (action === MEDICATION_NOTIFICATION_ACTION.SNOOZED && !result.alreadyAnswered) {
          ToastService.success(
            t('nkf.promptDetails.medicationSnoozed', { count: result.snoozeMinutes }),
          );
          await notificationEvents.clearDeliveredNotification(selected.itemUnique);
        }
      }
      if (session === auth.authStateVersion) {
        await inbox.refreshCount();
      }
    }
  } finally {
    saving.value = false;
  }
}

// 5. VueJS Hooks
watch([() => inbox.selected, locale], async (values, previous, onCleanup) => {
  const selected = inbox.selected;
  if (!selected || selected.language === locale.value) {
    return;
  }
  let active = true;
  onCleanup(() => {
    active = false;
  });
  const localized = await notificationsService.get(selected.itemUnique);
  if (active && inbox.selected === selected) {
    Object.assign(selected, localized);
  }
});
watch(
  () => inbox.selected,
  () => {
    responseRequest = null;
  },
  { flush: 'sync' },
);
</script>
